import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, FormFeedback, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Row, Table } from "reactstrap";
import Select from "react-select";
import { FaPen, FaPlus, FaTrash } from "react-icons/fa";

import api from "services/api";
import { useToast } from "hooks/toast";
import useDebounce from "hooks/useDebounce";
import PageHero, { PageHeroExportButton, PageHeroRefreshButton } from "components/PageHero";
import FilterBand from "components/FilterBand";
import MetricCard from "components/MetricCard";
import MetricCardGroup from "components/MetricCardGroup";
import ShellCard, { TableSurface } from "components/ShellCard";
import SortHeader from "components/SortHeader";
import TablePagination from "components/TablePagination";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { exportCsv } from "utils/exportCsv";
import { paginate, sortRows, toggleSort } from "utils/tableHelpers";
import { reactSelectPortalProps, reactSelectStyles } from "utils/reactSelectStyles";
import {
  ConfirmModal,
  ListState,
  STATUS_ALL,
  STATUS_OPTIONS,
  StatusPill,
  TEXTO_SOMENTE_CONSULTA,
  isActive,
  matchesStatus,
  mutedStyle,
  normalizeText,
  toastError,
  toastSuccess,
} from "./shared";

// Cadastro global simples (código, nome, descrição, status) no padrão do Hub:
// Modais e Tipos de serviço eram duas cópias da mesma tela, com limites e
// textos diferentes. A configuração (`config`) traz o que muda entre elas;
// a API (GET/POST/PUT/DELETE em config.url) segue a mesma.
//
// PO1-3836: com a prop `escopo`, a tela vira cadastro por embarcador (modelo
// misto do Tipo de serviço). Cada linha traz `idEmbarcador`/`embarcador`/`padrao`.
//   escopo.modo "cliente": lista padrões Everlog + os do embarcador do topo; só os
//     tipos do embarcador têm ações; Novo cria para o embarcador do topo.
//   escopo.modo "admin": lista tudo, filtra por escopo e escolhe no formulário se
//     o tipo é padrão Everlog ou de um embarcador.
// Sem `escopo` (Modais), nada muda.

const DEFAULT_SORT = { field: "codigo", dir: "asc" };
const ESCOPO_TODOS = "todos";
const ESCOPO_PADRAO = "padrao";
const ESCOPO_PROPRIO = "proprio";
const PADRAO_LABEL = "Padrão Everlog";

function emptyForm() {
  return { codigo: "", nome: "", descricao: "", status: "active", idEmbarcador: "" };
}

function ehPadrao(row) {
  return row.padrao === true || row.padrao === 1 || row.idEmbarcador === null || row.idEmbarcador === undefined;
}

function rotuloEscopo(row) {
  return ehPadrao(row) ? PADRAO_LABEL : row.embarcador || `Embarcador ${row.idEmbarcador}`;
}

function sortValue(row, field) {
  if (field === "status") return isActive(row.status) ? 1 : 0;
  if (field === "escopo") return ehPadrao(row) ? "" : rotuloEscopo(row);
  return row[field];
}

function matchesEscopo(row, filtro) {
  if (!filtro || filtro === ESCOPO_TODOS) return true;
  if (filtro === ESCOPO_PADRAO) return ehPadrao(row);
  if (filtro === ESCOPO_PROPRIO) return !ehPadrao(row);
  return !ehPadrao(row) && String(row.idEmbarcador) === String(filtro);
}

const EscopoPill = ({ row }) => (
  <span className={`cg-escopo ${ehPadrao(row) ? "cg-escopo--padrao" : "cg-escopo--embarcador"}`}>{rotuloEscopo(row)}</span>
);

export default function CadastroSimples({ config, somenteLeitura = false, escopo = null }) {
  const porEmbarcador = Boolean(escopo);
  const modoAdmin = porEmbarcador && escopo.modo === "admin";
  const modoCliente = porEmbarcador && !modoAdmin;
  const empresaId = modoCliente ? escopo.empresaId : null;
  const temEmpresa = modoCliente && Boolean(escopo.hasEmpresaSelection);
  const { addToast } = useToast();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_ALL);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const debouncedSearch = useDebounce(searchTerm, 250);

  const [isFormOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [escopoFilter, setEscopoFilter] = useState(ESCOPO_TODOS);
  const [embarcadores, setEmbarcadores] = useState([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const params = temEmpresa ? { idEmbarcador: empresaId } : undefined;
      const result = params ? await api.get(config.url, { params }) : await api.get(config.url);
      setData(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      setData([]);
      setLoadError(`Não foi possível buscar os ${config.plural} cadastrados.`);
      toastError(addToast, "Erro ao carregar", error, `Não foi possível buscar os ${config.plural} cadastrados.`);
    } finally {
      setLoading(false);
    }
  }, [addToast, config.plural, config.url, temEmpresa, empresaId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totais = useMemo(() => {
    const ativos = data.filter((item) => isActive(item.status)).length;
    const padroes = data.filter(ehPadrao).length;
    return { total: data.length, ativos, inativos: data.length - ativos, padroes, doEmbarcador: data.length - padroes };
  }, [data]);

  // Admin: opções do filtro de escopo a partir dos donos presentes na lista.
  const escopoOptions = useMemo(() => {
    if (!porEmbarcador) return [];
    const base = [
      { value: ESCOPO_TODOS, label: "Todos" },
      { value: ESCOPO_PADRAO, label: PADRAO_LABEL },
    ];
    if (modoCliente) return base.concat([{ value: ESCOPO_PROPRIO, label: "Do embarcador" }]);
    const donos = {};
    data.forEach((row) => {
      if (!ehPadrao(row)) donos[row.idEmbarcador] = rotuloEscopo(row);
    });
    return base.concat(
      Object.keys(donos)
        .map((id) => ({ value: id, label: donos[id] }))
        .sort((a, b) => a.label.localeCompare(b.label))
    );
  }, [data, porEmbarcador, modoCliente]);

  // Admin: lista de embarcadores para o campo "Escopo" do formulário.
  useEffect(() => {
    if (!modoAdmin || !isFormOpen || embarcadores.length) return;
    api
      .get("/cadastros-gerais/embarcadores")
      .then((res) => {
        const rows = Array.isArray(res.data) ? res.data : [];
        setEmbarcadores(
          rows
            .map((e) => ({ value: String(e.id), label: e.fantasia || e.razaoSocial || e.nome || `Embarcador ${e.id}` }))
            .sort((a, b) => a.label.localeCompare(b.label))
        );
      })
      .catch(() => {});
  }, [modoAdmin, isFormOpen, embarcadores.length]);

  const filtered = useMemo(() => {
    const query = normalizeText(debouncedSearch);
    return data.filter((item) => {
      if (!matchesStatus(item.status, statusFilter)) return false;
      if (porEmbarcador && !matchesEscopo(item, escopoFilter)) return false;
      if (!query) return true;
      return normalizeText([item.codigo, item.nome, item.descricao].filter(Boolean).join(" ")).includes(query);
    });
  }, [data, debouncedSearch, statusFilter, escopoFilter, porEmbarcador]);

  const sorted = useMemo(() => sortRows(filtered, sort, sortValue), [filtered, sort]);
  const paged = useMemo(() => paginate(sorted, page, pageSize), [sorted, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, escopoFilter, sort, pageSize]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setForm(emptyForm());
    setFormErrors({});
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((item) => {
    setEditing(item);
    setForm({
      codigo: item.codigo || "",
      nome: item.nome || "",
      descricao: item.descricao || "",
      status: isActive(item.status) ? "active" : "inactive",
      idEmbarcador: item.idEmbarcador ? String(item.idEmbarcador) : "",
    });
    setFormErrors({});
    setFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    if (!saving) setFormOpen(false);
  }, [saving]);

  const updateForm = useCallback((field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  }, []);

  const validateForm = () => {
    const errors = {};
    const codigo = form.codigo.trim();
    const nome = form.nome.trim();
    if (config.codigo.required && !codigo) errors.codigo = "Informe o código.";
    else if (codigo.length > config.codigo.max) errors.codigo = `Máximo de ${config.codigo.max} caracteres.`;
    if (!nome) errors.nome = `Informe o nome do ${config.singular}.`;
    else if (nome.length > config.nome.max) errors.nome = `Máximo de ${config.nome.max} caracteres.`;
    if (form.descricao.trim().length > config.descricao.max) errors.descricao = `Máximo de ${config.descricao.max} caracteres.`;
    return errors;
  };

  const handleSave = async () => {
    const nextErrors = validateForm();
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      return;
    }
    const payload = {
      codigo: form.codigo.trim(),
      nome: form.nome.trim(),
      descricao: form.descricao.trim(),
      status: form.status === "active",
    };
    // PO1-3836: admin escolhe o escopo ("" = padrão Everlog); cliente cria para o
    // embarcador do topo e nunca muda o dono na edição.
    if (modoAdmin) payload.idEmbarcador = form.idEmbarcador || "";
    if (modoCliente && !editing) payload.idEmbarcador = empresaId;
    try {
      setSaving(true);
      if (editing) await api.put(`${config.url}/${editing.id}`, payload);
      else await api.post(config.url, payload);
      toastSuccess(
        addToast,
        editing ? `${config.titulo} atualizado` : `${config.titulo} cadastrado`,
        editing ? "As alterações foram salvas." : `O ${config.singular} foi cadastrado com sucesso.`
      );
      setFormOpen(false);
      loadData();
    } catch (error) {
      toastError(addToast, "Erro ao salvar", error, `Não foi possível salvar o ${config.singular}.`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      setDeleting(true);
      await api.delete(`${config.url}/${toDelete.id}`);
      toastSuccess(addToast, `${config.titulo} excluído`, `O ${config.singular} "${toDelete.nome}" foi removido.`);
      setToDelete(null);
      loadData();
    } catch (error) {
      toastError(addToast, "Não foi possível excluir", error, `Erro ao excluir o ${config.singular}.`);
      setToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const exportar = () =>
    exportCsv({
      filename: config.exportFilename,
      columns: [
        { key: "codigo", label: "Código" },
        { key: "nome", label: "Nome" },
        { key: "descricao", label: "Descrição" },
        { label: "Status", getValue: (row) => (isActive(row.status) ? "Ativo" : "Inativo") },
      ].concat(porEmbarcador ? [{ label: "Escopo", getValue: rotuloEscopo }] : []),
      rows: sorted,
    });

  const handleSort = useCallback((field) => setSort((prev) => toggleSort(prev, field)), []);
  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter(STATUS_ALL);
    setEscopoFilter(ESCOPO_TODOS);
  };
  const hasFilters = normalizeText(searchTerm) !== "" || statusFilter !== STATUS_ALL || escopoFilter !== ESCOPO_TODOS;
  const escopoOption = escopoOptions.find((o) => o.value === escopoFilter) || escopoOptions[0] || null;
  const toggleEscopoFilter = (value) => setEscopoFilter((current) => (current === value ? ESCOPO_TODOS : value));
  // Cliente só mexe nos tipos do próprio embarcador; padrão Everlog é do admin.
  const podeEditar = (item) => !somenteLeitura && (!porEmbarcador || modoAdmin || !ehPadrao(item));
  const temAcoes = !somenteLeitura;
  const novoBloqueado = modoCliente && !temEmpresa;
  const descricaoHero = porEmbarcador
    ? modoAdmin
      ? config.descriptionAdmin || config.description
      : config.descriptionCliente || config.description
    : config.description;
  const subtituloModal = porEmbarcador
    ? modoAdmin
      ? "Padrão Everlog vale para todos os embarcadores; um tipo de embarcador só aparece para ele."
      : `Tipo de serviço exclusivo de ${escopo.empresaNome || "deste embarcador"}.`
    : "Cadastro global: vale para todos os embarcadores.";
  const statusOption = STATUS_OPTIONS.find((o) => o.value === statusFilter) || STATUS_OPTIONS[0];
  const toggleStatusFilter = (value) => setStatusFilter((current) => (current === value ? STATUS_ALL : value));

  const codigoField = (
    <FormGroup>
      <Label for="cad-codigo">Código{config.codigo.required ? "" : " (opcional)"}</Label>
      <Input
        id="cad-codigo"
        value={form.codigo}
        maxLength={config.codigo.max}
        onChange={(e) => updateForm("codigo", e.target.value)}
        invalid={Boolean(formErrors.codigo)}
        placeholder={config.codigo.placeholder}
      />
      {formErrors.codigo ? <FormFeedback>{formErrors.codigo}</FormFeedback> : null}
    </FormGroup>
  );
  const nomeField = (
    <FormGroup>
      <Label for="cad-nome">Nome</Label>
      <Input
        id="cad-nome"
        value={form.nome}
        maxLength={config.nome.max}
        onChange={(e) => updateForm("nome", e.target.value)}
        invalid={Boolean(formErrors.nome)}
        placeholder={config.nome.placeholder}
      />
      {formErrors.nome ? <FormFeedback>{formErrors.nome}</FormFeedback> : null}
    </FormGroup>
  );

  const listState = (
    <ListState
      loading={loading}
      error={loadError}
      empty={sorted.length === 0}
      loadingText={`Carregando ${config.plural}…`}
      emptyTitle={`Nenhum ${config.singular} encontrado`}
      emptyText={
        hasFilters
          ? "Ajuste os filtros ou limpe a busca."
          : somenteLeitura
          ? TEXTO_SOMENTE_CONSULTA
          : `Cadastre o primeiro ${config.singular} pelo botão acima.`
      }
      onRetry={loadData}
    />
  );
  const showState = loading || loadError || sorted.length === 0;

  return (
    <div className={`content ${config.pageClass}`}>
      <Modal isOpen={isFormOpen} toggle={closeForm} modalClassName="modal-align-top" fade={false} size="md">
        <ModalHeader toggle={closeForm} tag="h5">
          {editing ? `Editar ${config.singular}` : config.novo}
        </ModalHeader>
        <ModalBody style={{ padding: "1.25rem 1.5rem" }}>
          <div className="cg-modal-subtitle">{subtituloModal}</div>
          {modoAdmin ? (
            <FormGroup>
              <Label for="cad-escopo">Escopo</Label>
              <Select
                inputId="cad-escopo"
                classNamePrefix="react-select"
                styles={reactSelectStyles}
                {...reactSelectPortalProps()}
                options={[{ value: "", label: PADRAO_LABEL }].concat(embarcadores)}
                value={
                  form.idEmbarcador
                    ? embarcadores.find((o) => o.value === form.idEmbarcador) || {
                        value: form.idEmbarcador,
                        label: (editing && editing.embarcador) || `Embarcador ${form.idEmbarcador}`,
                      }
                    : { value: "", label: PADRAO_LABEL }
                }
                onChange={(opt) => updateForm("idEmbarcador", opt ? opt.value : "")}
                placeholder="Padrão Everlog ou um embarcador"
              />
            </FormGroup>
          ) : null}
          <Row>
            <Col md="4">{codigoField}</Col>
            <Col md="8">{nomeField}</Col>
          </Row>
          <FormGroup>
            <Label for="cad-descricao">Descrição (opcional)</Label>
            <Input
              id="cad-descricao"
              type="textarea"
              rows="3"
              value={form.descricao}
              maxLength={config.descricao.max}
              onChange={(e) => updateForm("descricao", e.target.value)}
              invalid={Boolean(formErrors.descricao)}
            />
            {formErrors.descricao ? <FormFeedback>{formErrors.descricao}</FormFeedback> : null}
          </FormGroup>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="cad-status">Status</Label>
                <Input id="cad-status" type="select" value={form.status} onChange={(e) => updateForm("status", e.target.value)}>
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <div className="cg-modal-footer">
            <Button color="default" outline size="sm" onClick={closeForm} disabled={saving}>
              Cancelar
            </Button>
            <Button color="info" size="sm" onClick={handleSave} disabled={saving} data-testid="salvar">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <ConfirmModal
        isOpen={Boolean(toDelete)}
        title={`Excluir ${config.singular}`}
        confirmLabel="Excluir"
        busyLabel="Excluindo..."
        busy={deleting}
        onConfirm={handleDelete}
        onCancel={() => setToDelete(null)}
      >
        Confirma a exclusão do {config.singular} <strong>{toDelete ? toDelete.nome : ""}</strong>? Esta ação não pode ser
        desfeita. Se ele já estiver em uso, prefira editar e marcar como inativo.
      </ConfirmModal>

      <PageHero
        eyebrow={somenteLeitura || modoCliente ? "Cadastros gerais" : "Cadastros globais"}
        title={config.heroTitle}
        description={somenteLeitura ? `${descricaoHero} ${TEXTO_SOMENTE_CONSULTA}` : descricaoHero}
        actions={
          <>
            <PageHeroRefreshButton onClick={loadData} loading={loading} />
            <PageHeroExportButton onClick={exportar} disabled={loading || sorted.length === 0} />
            {somenteLeitura ? null : (
              <Button
                color="info"
                className="btn-sm"
                onClick={openCreate}
                disabled={novoBloqueado}
                title={novoBloqueado ? "Selecione um embarcador no topo para cadastrar um tipo dele" : undefined}
              >
                <FaPlus style={{ marginRight: 6 }} />
                {config.novo}
              </Button>
            )}
          </>
        }
      />

      <MetricCardGroup>
        <MetricCard
          label={`${config.heroTitle} cadastrados`}
          value={totais.total}
          helper="clique para ver todos"
          iconClassName={config.icon}
          tone="info"
          onClick={() => setStatusFilter(STATUS_ALL)}
          active={statusFilter === STATUS_ALL}
        />
        <MetricCard
          label="Ativos"
          value={totais.ativos}
          helper="em uso"
          iconClassName="tim-icons icon-check-2"
          tone="success"
          onClick={() => toggleStatusFilter("active")}
          active={statusFilter === "active"}
        />
        <MetricCard
          label="Inativos"
          value={totais.inativos}
          helper="desligados"
          iconClassName="tim-icons icon-simple-remove"
          tone="neutral"
          onClick={() => toggleStatusFilter("inactive")}
          active={statusFilter === "inactive"}
        />
        {porEmbarcador ? (
          <MetricCard
            label={PADRAO_LABEL}
            value={totais.padroes}
            helper={modoAdmin ? `${totais.doEmbarcador} de embarcadores` : `${totais.doEmbarcador} do embarcador`}
            iconClassName="tim-icons icon-world"
            tone="info"
            onClick={() => toggleEscopoFilter(ESCOPO_PADRAO)}
            active={escopoFilter === ESCOPO_PADRAO}
          />
        ) : null}
      </MetricCardGroup>

      <FilterBand
        mode="client"
        searchLabel={`Buscar ${config.singular}`}
        searchHint="Código, nome ou descrição"
        searchPlaceholder="Buscar por código, nome ou descrição"
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        activeFiltersCount={(statusFilter !== STATUS_ALL ? 1 : 0) + (escopoFilter !== ESCOPO_TODOS ? 1 : 0)}
        panelTitle="Filtros"
        panelDescription={
          porEmbarcador ? `Refine a lista pelo status e pelo escopo do ${config.singular}.` : `Refine a lista pelo status do ${config.singular}.`
        }
        onClear={hasFilters ? clearFilters : undefined}
        showActions={hasFilters}
      >
        <Row>
          <Col md="4">
            <Label for="cad-status-filter" style={{ color: AUTOPILOT_COLORS.muted }}>
              Status
            </Label>
            <Select
              inputId="cad-status-filter"
              classNamePrefix="react-select"
              styles={reactSelectStyles}
              {...reactSelectPortalProps()}
              options={STATUS_OPTIONS}
              value={statusOption}
              onChange={(opt) => setStatusFilter(opt ? opt.value : STATUS_ALL)}
              isSearchable={false}
            />
          </Col>
          {porEmbarcador ? (
            <Col md="4">
              <Label for="cad-escopo-filter" style={{ color: AUTOPILOT_COLORS.muted }}>
                Escopo
              </Label>
              <Select
                inputId="cad-escopo-filter"
                classNamePrefix="react-select"
                styles={reactSelectStyles}
                {...reactSelectPortalProps()}
                options={escopoOptions}
                value={escopoOption}
                onChange={(opt) => setEscopoFilter(opt ? opt.value : ESCOPO_TODOS)}
                isSearchable={modoAdmin}
              />
            </Col>
          ) : null}
        </Row>
      </FilterBand>

      <ShellCard title={`${config.heroTitle} cadastrados`} description={`${sorted.length} resultado(s)`}>
        {showState ? (
          listState
        ) : (
          <TableSurface>
            <Table size="sm" responsive hover className="cg-table hub-thead-accent" data-testid="cad-tabela">
              <colgroup>
                <col style={{ width: 120 }} />
                <col style={{ width: "28%" }} />
                <col />
                {porEmbarcador ? <col style={{ width: 190 }} /> : null}
                <col style={{ width: 110 }} />
                {temAcoes ? <col style={{ width: 100 }} /> : null}
              </colgroup>
              <thead className="text-primary">
                <tr>
                  <th>
                    <SortHeader label="Código" field="codigo" sort={sort} onSort={handleSort} />
                  </th>
                  <th>
                    <SortHeader label="Nome" field="nome" sort={sort} onSort={handleSort} />
                  </th>
                  <th>
                    <SortHeader label="Descrição" field="descricao" sort={sort} onSort={handleSort} />
                  </th>
                  {porEmbarcador ? (
                    <th>
                      <SortHeader label="Escopo" field="escopo" sort={sort} onSort={handleSort} />
                    </th>
                  ) : null}
                  <th>
                    <SortHeader label="Status" field="status" sort={sort} onSort={handleSort} />
                  </th>
                  {temAcoes ? <th className="text-right">Ações</th> : null}
                </tr>
              </thead>
              <tbody>
                {paged.items.map((item) => (
                  <tr key={item.id}>
                    <td className="cg-mono">{item.codigo || <span style={mutedStyle}>—</span>}</td>
                    <td>
                      <strong>{item.nome}</strong>
                    </td>
                    <td style={item.descricao ? undefined : mutedStyle}>{item.descricao || "—"}</td>
                    {porEmbarcador ? (
                      <td>
                        <EscopoPill row={item} />
                      </td>
                    ) : null}
                    <td>
                      <StatusPill active={isActive(item.status)} />
                    </td>
                    {temAcoes && !podeEditar(item) ? (
                      <td className="text-right" style={mutedStyle} title="Mantido pela Everlog">
                        —
                      </td>
                    ) : null}
                    {temAcoes && podeEditar(item) ? (
                      <td className="text-right">
                        <span className="cg-acoes">
                          <Button color="link" size="sm" className="btn-icon" title="Editar" onClick={() => openEdit(item)}>
                            <FaPen />
                          </Button>
                          <Button color="link" size="sm" className="btn-icon text-danger" title="Excluir" onClick={() => setToDelete(item)}>
                            <FaTrash />
                          </Button>
                        </span>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </Table>
            <TablePagination
              page={paged.page}
              pageSize={pageSize}
              total={paged.total}
              itemLabel={`${config.plural}`}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          </TableSurface>
        )}
      </ShellCard>
    </div>
  );
}
